import { useCallback, useState, useRef, useEffect } from "react";
import { Input } from "antd";
import { Search } from "lucide-react";
import "./index.css";
import IMAGES from "../../../../constant/images";

// Reusable class constants
const CLASSES = {
  CONTAINER: 'bg-white rounded-xl p-2 w-full',
  TITLE: 'font-poppins font-semibold text-base text-[#171717] leading-6 mb-0',
  SUBTITLE: 'font-poppins font-normal text-xs text-[#64748B] leading-[18px]',
  SEARCH_BOX: 'relative w-[300px] max-md:w-full',
  SEARCH_INPUT: 'bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm h-9 pl-10 text-sm text-[#171717] placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#B3D7FF] font-poppins',
  SEARCH_ICON: 'absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none',
  ACTION_BTN: 'flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition',
  ACTIONS_WRAPPER: 'flex items-center gap-3 max-md:flex-wrap max-md:gap-2'
};

export default function Index({ searchTerm, setSearchTerm, onSearch, onFilterToggle, onSortChange }) {
  const [sortOpen, setSortOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState(null);
  const dropdownRef = useRef();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Trigger search immediately (like appointments)
    if (onSearch) {
      onSearch(value);
    }
  }, [setSearchTerm, onSearch]);

  const handleSortChange = (field, order) => {
    setCurrentSort({ field, order });
    if (onSortChange) {
      onSortChange({ field, order });
    }
    setSortOpen(false);
  };

  const handleClearSort = () => {
    setCurrentSort(null);
    if (onSortChange) {
      onSortChange(null);
    }
    setSortOpen(false);
  };

  const isCurrentSort = (field, order) => {
    return currentSort?.field === field && currentSort?.order === order;
  };

  return (
    <div className={CLASSES.CONTAINER}>
      <div className="flex justify-between items-center w-full gap-4 max-md:flex-col max-md:items-start">

        {/* Title & Subtitle */}
        <div className="flex flex-col gap-1">
          <h4 className={CLASSES.TITLE}>All Practitioners</h4>
          <span className={CLASSES.SUBTITLE}>
            Manage the roster of approved and active healthcare professionals.
          </span>
        </div>

        {/* Actions */}
        <div className={CLASSES.ACTIONS_WRAPPER}>

          {/* Search */}
          <div className={CLASSES.SEARCH_BOX}>
            <Input
              placeholder="Search by Practitioner Name"
              className={CLASSES.SEARCH_INPUT}
              aria-label="Search practitioners"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Search size={16} className={CLASSES.SEARCH_ICON} />
          </div>

          {/* Filter */}
          <button
            className={CLASSES.ACTION_BTN}
            onClick={onFilterToggle}
            aria-label="Open advanced filters"
          >
            Filter
            <img src={IMAGES.filter} alt="filter" className="w-4 h-4" />
          </button>

          {/* Sort Dropdown */}
          <div className="relative inline-block" ref={dropdownRef}>
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="relative flex items-center justify-center h-9 w-9 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition"
              aria-label="Sort practitioners"
            >
              <img src={IMAGES.sorting_icon} alt="sort" className="w-4 h-5" />
              {currentSort && (
                <span className="absolute -top-1 -right-1 bg-[#2563EB] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  1
                </span>
              )}
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-lg border-[1px] border-solid border-[#E2E8F0] py-2 z-50 font-poppins text-sm text-[#334155]">
                {/* Clear Sort Option */}
                {currentSort && (
                  <div
                    className="px-4 py-3 hover:bg-[#F8FAFC] cursor-pointer border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] text-[#64748B] font-medium"
                    onClick={handleClearSort}
                  >
                    ✕ Clear Sorting
                  </div>
                )}

                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("name", "asc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "asc")}
                >
                  {isCurrentSort("name", "asc") && "✓ "} Sort by Name (A-Z)
                </div>
                <div
                  className={`px-4 py-3 cursor-pointer ${
                    isCurrentSort("name", "desc")
                      ? "bg-[#EFF6FF] text-[#2563EB] font-semibold"
                      : "hover:bg-[#F8FAFC]"
                  }`}
                  onClick={() => handleSortChange("name", "desc")}
                >
                  {isCurrentSort("name", "desc") && "✓ "} Sort by Name (Z-A)
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
